import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "safemama.customer.session";
const EMAIL_KEY = "safemama.customer.email";

const PLAN_LABEL = {
  single: "Single Edition · ₹699",
  "3month": "3-Month Plan · ₹1,799",
  "6month": "6-Month Plan · ₹2,999",
};

/* ============ Sign-in with email OTP ============ */
function SignIn({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // 'email' | 'code'
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devCode, setDevCode] = useState(null);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/customer/request-code`, { email });
      setStep("code");
      if (res.data.dev_code) {
        setDevCode(res.data.dev_code);
        toast.success(`Dev code: ${res.data.dev_code}`);
      } else {
        toast.success("Check your email for a 6-digit code.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send code. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/customer/verify-code`, { email, code });
      localStorage.setItem(TOKEN_KEY, res.data.session_token);
      localStorage.setItem(EMAIL_KEY, res.data.email);
      onSignedIn(res.data.session_token, res.data.email);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not verify. Try again.";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="pt-10 md:pt-16" data-testid="manage-signin">
      <section className="editorial-container">
        <SectionLabel number="No. 007" label="Manage my plan" />
        <h1 className="mt-6 font-serif text-[13vw] sm:text-[9vw] md:text-[7vw] lg:text-[6rem] leading-[1] tracking-tight text-ink pb-4">
          <MaskedReveal text="Sign in" as="span" className="block" delay={0.1} animate />
          <MaskedReveal text="by post." as="span" className="block italic text-terracotta" delay={0.35} animate />
        </h1>
        <FadeUp delay={0.6}>
          <p className="mt-8 font-serif text-xl md:text-2xl leading-[1.4] text-ink/75 max-w-2xl">
            Enter the email address you used when you subscribed. We'll send you a six-digit code.
          </p>
        </FadeUp>
      </section>

      <section className="mt-16 editorial-container">
        <div className="max-w-lg">
          {step === "email" ? (
            <form onSubmit={requestCode}>
              <label className="overline text-ink/60 block mb-3">Your email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
                placeholder="you@post.com"
                data-testid="manage-email-input"
                autoFocus
              />
              <button type="submit" className="btn-pill mt-10 disabled:opacity-60" disabled={sending} data-testid="manage-send-code">
                {sending ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify}>
              <div className="overline text-ink/60">Code sent to</div>
              <div className="mt-2 font-serif italic text-2xl text-ink">{email}</div>
              <label className="overline text-ink/60 block mt-8 mb-3">6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-4xl tracking-widest text-ink outline-none focus:border-ink transition-colors"
                placeholder="••••••"
                data-testid="manage-code-input"
                autoFocus
              />
              {devCode && (
                <p className="mt-3 text-xs text-ink/50 italic">Dev: {devCode}</p>
              )}
              <div className="mt-10 flex gap-4">
                <button type="submit" className="btn-pill disabled:opacity-60" disabled={verifying} data-testid="manage-verify">
                  {verifying ? "Verifying…" : "Continue"}
                </button>
                <button type="button" onClick={() => setStep("email")} className="btn-pill ghost">
                  Use a different email
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

/* ============ Row inline editor for a single subscription ============ */
function SubscriptionCard({ sub, refresh, token }) {
  const auth = { headers: { "X-Customer-Session": token } };
  const [editMode, setEditMode] = useState(null); // 'address' | 'edition' | 'preferences' | null

  const [phone, setPhone] = useState(sub.phone || "");
  const [line1, setLine1] = useState(sub.address?.line1 || "");
  const [line2, setLine2] = useState(sub.address?.line2 || "");
  const [city, setCity] = useState(sub.address?.city || "");
  const [state, setState] = useState(sub.address?.state || "");
  const [pincode, setPincode] = useState(sub.address?.pincode || "");
  const [preferences, setPreferences] = useState(sub.preferences || "");
  const [nextEdition, setNextEdition] = useState(sub.edition);

  const isSingle = sub.total_editions === 1;
  const isCompleted = sub.status === "completed";
  const isPaused = sub.status === "paused";
  const canEdit = !isCompleted && sub.editions_remaining > 0;

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      refresh();
      setEditMode(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
  };

  const pause = () => call(() => axios.post(`${API}/customer/subscriptions/${sub.id}/pause`, {}, auth), "Deliveries paused.");
  const resume = () => call(() => axios.post(`${API}/customer/subscriptions/${sub.id}/resume`, {}, auth), "Deliveries resumed.");
  const saveAddress = () => call(() => axios.post(`${API}/customer/subscriptions/${sub.id}/address`, {
    phone,
    address: { line1, line2, city, state, pincode, country: "IN" },
  }, auth), "Address updated for future editions.");
  const saveEdition = () => call(() => axios.post(`${API}/customer/subscriptions/${sub.id}/edition`, { edition: nextEdition }, auth), "Edition switched for remaining envelopes.");
  const savePrefs = () => call(() => axios.post(`${API}/customer/subscriptions/${sub.id}/preferences`, { preferences }, auth), "Preferences saved.");

  return (
    <div className="border-t border-ink/20 pt-8" data-testid={`subscription-${sub.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <div className="overline text-ink/50">Your Safe Mama Editorial</div>
          <h2
            className={`mt-3 font-serif italic text-4xl md:text-5xl leading-[1.05] ${
              sub.edition === "motherhood" ? "text-sage" : "text-terracotta"
            }`}
          >
            {sub.edition === "motherhood" ? "Motherhood Edition" : "Pregnancy Edition"}
          </h2>
          <div className="mt-3 font-serif text-lg text-ink/70">{PLAN_LABEL[sub.package_id] || sub.package_id}</div>
        </div>
        <div className="text-right">
          <div className="font-serif text-6xl text-ink leading-none" data-testid={`remaining-${sub.id}`}>
            {sub.editions_remaining}
            <span className="text-ink/40"> / {sub.total_editions}</span>
          </div>
          <div className="overline text-ink/50 mt-2">Envelopes remaining</div>
          <div className="text-xs text-ink/60 mt-2">{sub.editions_dispatched} dispatched</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <span
          className={`inline-block px-3 py-1 rounded-full border text-xs uppercase tracking-wider ${
            isPaused
              ? "bg-ink/10 text-ink/70 border-ink/20"
              : isCompleted
                ? "bg-ink/10 text-ink/60 border-ink/20"
                : "bg-sage/15 text-sage border-sage/30"
          }`}
        >
          {sub.status}
        </span>
        {isPaused && (
          <span className="font-serif italic text-ink/70">
            Your deliveries are paused. You still have {sub.editions_remaining} edition{sub.editions_remaining === 1 ? "" : "s"} remaining — we'll hold them until you're ready.
          </span>
        )}
        {isCompleted && (
          <span className="font-serif italic text-ink/60">
            This plan is completed. To receive more editions, purchase another plan.
          </span>
        )}
      </div>

      {/* Actions */}
      {canEdit && (
        <div className="mt-8 flex flex-wrap gap-3">
          {!isSingle && (isPaused ? (
            <button onClick={resume} className="btn-pill" data-testid={`resume-${sub.id}`}>Resume deliveries</button>
          ) : (
            <button onClick={pause} className="btn-pill ghost" data-testid={`pause-${sub.id}`}>Pause deliveries</button>
          ))}
          <button onClick={() => setEditMode(editMode === "address" ? null : "address")} className="btn-pill ghost" data-testid={`edit-address-${sub.id}`}>
            {editMode === "address" ? "Close" : "Update delivery details"}
          </button>
          {!isSingle && (
            <button onClick={() => setEditMode(editMode === "edition" ? null : "edition")} className="btn-pill ghost" data-testid={`edit-edition-${sub.id}`}>
              {editMode === "edition" ? "Close" : "Change edition / stage"}
            </button>
          )}
          <button onClick={() => setEditMode(editMode === "preferences" ? null : "preferences")} className="btn-pill ghost" data-testid={`edit-preferences-${sub.id}`}>
            {editMode === "preferences" ? "Close" : "Update interests"}
          </button>
        </div>
      )}

      {editMode === "address" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-x-10 border-t border-ink/15 pt-8">
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-2">Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink" />
          </div>
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-2">PIN</label>
            <input value={pincode} onChange={(e) => setPincode(e.target.value)} className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink" />
          </div>
          <div className="md:col-span-12">
            <label className="overline text-ink/60 block mb-2">Address line 1</label>
            <input value={line1} onChange={(e) => setLine1(e.target.value)} className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink" />
          </div>
          <div className="md:col-span-12">
            <label className="overline text-ink/60 block mb-2">Address line 2</label>
            <input value={line2} onChange={(e) => setLine2(e.target.value)} className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink" />
          </div>
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-2">City</label>
            <input value={city} onChange={(e) => setCity(e.target.value)} className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink" />
          </div>
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-2">State</label>
            <input value={state} onChange={(e) => setState(e.target.value)} className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink" />
          </div>
          <div className="md:col-span-12">
            <button onClick={saveAddress} className="btn-pill" data-testid={`save-address-${sub.id}`}>Save address</button>
          </div>
        </div>
      )}

      {editMode === "edition" && (
        <div className="mt-8 border-t border-ink/15 pt-8">
          <p className="font-serif text-lg text-ink/75 max-w-2xl">
            Change the remaining envelopes to a different edition. Envelopes already dispatched will not
            change.
          </p>
          <div className="mt-6 flex gap-6">
            {[
              { id: "pregnancy", label: "Pregnancy Edition" },
              { id: "motherhood", label: "Motherhood Edition" },
            ].map((e) => (
              <button
                key={e.id}
                onClick={() => setNextEdition(e.id)}
                className={`font-serif italic text-2xl md:text-3xl ${
                  nextEdition === e.id
                    ? e.id === "motherhood" ? "text-sage" : "text-terracotta"
                    : "text-ink/30 hover:text-ink/70"
                }`}
              >
                {e.label}
                {nextEdition === e.id && <span className="ml-3 inline-block h-1.5 w-1.5 rounded-full bg-current align-middle" />}
              </button>
            ))}
          </div>
          <button onClick={saveEdition} className="btn-pill mt-8" data-testid={`save-edition-${sub.id}`}>Save edition</button>
        </div>
      )}

      {editMode === "preferences" && (
        <div className="mt-8 border-t border-ink/15 pt-8">
          <p className="font-serif text-lg text-ink/75 max-w-2xl">
            Tell us what you'd love to read about. We'll try to shape your future editions around it.
          </p>
          <textarea
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            rows={4}
            className="mt-6 w-full bg-transparent border-b border-ink/30 py-2 font-serif text-lg text-ink outline-none focus:border-ink resize-none"
            placeholder="e.g. postpartum nutrition, sleep, second pregnancies…"
          />
          <button onClick={savePrefs} className="btn-pill mt-6" data-testid={`save-preferences-${sub.id}`}>Save preferences</button>
        </div>
      )}
    </div>
  );
}

/* ============ Main ============ */
export default function ManagePlan() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/subscriptions`, { headers: { "X-Customer-Session": t } });
      setSubs(res.data.subscriptions);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMAIL_KEY);
        setToken("");
        toast.error("Please sign in again.");
      } else {
        toast.error("Could not load your subscriptions.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSignOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken("");
    setEmail("");
    setSubs([]);
  };

  if (!token) {
    return <SignIn onSignedIn={(t, e) => { setToken(t); setEmail(e); }} />;
  }

  return (
    <div className="pt-10 md:pt-16" data-testid="manage-page">
      <section className="editorial-container">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <SectionLabel number="No. 007" label="Manage my plan" />
            <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-tight text-ink">Welcome back.</h1>
            <p className="mt-4 font-serif italic text-lg text-ink/70">Signed in as {email}</p>
          </div>
          <button onClick={onSignOut} className="btn-pill ghost" data-testid="manage-signout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </section>

      <section className="mt-16 editorial-container">
        {loading ? (
          <p className="font-serif italic text-ink/60">Loading…</p>
        ) : subs.length === 0 ? (
          <div className="max-w-2xl">
            <p className="font-serif text-xl text-ink/75">
              We don't have any subscriptions on file for this email yet. If you've just paid, please
              give us a moment for the payment to complete.
            </p>
          </div>
        ) : (
          <div className="space-y-16 md:space-y-24">
            {subs.map((s) => (
              <SubscriptionCard key={s.id} sub={s} refresh={() => load(token)} token={token} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
